import { createFileRoute } from "@tanstack/react-router";
import { canonicalLink, canonicalUrl, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { ServicePage } from "@/components/ServicePage";
import { SaSiblingCallouts } from "./patio-covers.in.san-antonio";
import hero from "@/assets/original/stock-curtains.jpg";
import heroPicture from "@/assets/original/stock-curtains.jpg?w=640;1024;1600;1920&format=avif;webp;jpeg&as=picture";

const PATH = "/patio-enclosures/in/san-antonio";
const TITLE = "Patio Enclosures in San Antonio, TX | Texas Made Shade";
const DESC = "Custom patio enclosures in San Antonio, TX — clear-vinyl drop curtains, Phifertex screen walls, motorized roll-downs. Turn your covered patio into a 3-season room. Free on-site quotes across Bexar County.";

export const Route = createFileRoute("/patio-enclosures/in/san-antonio")({
  head: (ctx) => ({
    links: [canonicalLink(PATH)],
    meta: routeMeta(ctx, {
      title: TITLE,
      description: DESC,
      image: hero,
      imageAlt: "Clear vinyl drop curtain patio enclosure installed in San Antonio",
      socialTitle: "Patio Enclosures in San Antonio, TX",
      socialDescription: DESC,
    }),
    scripts: [
      ...serviceHeadScripts({
        path: PATH,
        serviceName: "Patio Enclosures in San Antonio, TX",
        breadcrumbName: "Patio Enclosures in San Antonio",
        description: DESC,
        faqs: [
          { q: "What's the difference between clear vinyl and screen?", a: "Clear vinyl (20-gauge or 30-gauge marine vinyl) blocks wind and rain while staying see-through — best for winter use. Phifertex screen blocks bugs, sun and 90% UV while still letting breeze through — best for spring through fall on a San Antonio patio." },
          { q: "Can you motorize the drop curtains?", a: "Yes — we install Somfy-powered motorized roll-down enclosures with wall-switch or app control. Common on larger Stone Oak and Alamo Heights patios where you want to drop all four walls at once." },
          { q: "Will an enclosure work with my existing patio cover?", a: "Almost always. We mount tracks or zipper systems to existing wood, aluminum or stucco fascia. We come measure and confirm the fascia is structural before we quote." },
          { q: "Do you build screened-in porches in San Antonio?", a: "Yes — full Phifertex screen walls with zipper doors, framed in welded aluminum. We can also rebuild aging wood screen porches with rust-proof aluminum framing." },
        ],
      }),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Patio Enclosures in San Antonio, TX",
          serviceType: "Patio Enclosure Installation",
          url: canonicalUrl(PATH),
          provider: { "@id": "https://texasmadeshade.com/#business" },
          areaServed: { "@type": "City", name: "San Antonio", containedInPlace: { "@type": "AdministrativeArea", name: "Bexar County" } },
        }),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <ServicePage
      currentPath={PATH}
      serviceName="Patio Enclosures in San Antonio"
      breadcrumbs={[
        { label: "Patio Covers", to: "/patio-covers/in/san-antonio" },
        { label: "Patio Enclosures in San Antonio, TX" },
      ]}
      eyebrow="★ PATIO ENCLOSURES · SAN ANTONIO, TX ★"
      title="Custom Patio Enclosures in San Antonio, TX"
      intro="Turn your San Antonio covered patio into a 3-season room with custom drop curtains, screen walls and motorized roll-downs. Block wind, rain, mosquitos and Bexar County dust without giving up the view."
      heroImage={heroPicture}
      heroCta={{
        eyebrow: "Drop Curtains & Screen Walls · San Antonio, TX",
        headline: "Patio Enclosure Quote — Free On-Site Measure",
        subhead: "Send photos of your covered patio with rough dimensions. We'll quote vinyl, screen and motorized options in one estimate.",
        primaryHref: `/#lets-talk?type=${encodeURIComponent("Drop Curtain")}&notes=${encodeURIComponent("Patio enclosure — San Antonio, TX.")}`,
        primaryLabel: "Get My Enclosure Quote",
        phoneNumber: "+1 (830) 390-9588",
      }}
      features={[
        "20-gauge & 30-gauge clear marine vinyl drop curtains",
        "Phifertex Plus screen walls (90% UV block, bug-tight)",
        "Motorized Somfy roll-down systems",
        "Zipper, snap & track mounting options",
        "Fits existing wood, aluminum or stucco fascia",
        "Free on-site measure across San Antonio",
      ]}
      body={[
        {
          heading: "Three-Season Patios in a 4-Season City",
          text: "San Antonio winters are mild but windy, summers are full of mosquitos, and shoulder seasons are perfect — if you can keep the breeze without the bugs. A custom enclosure makes your patio usable about 320 days a year instead of 180.",
        },
        {
          heading: "Vinyl, Screen or Both",
          text: "Most San Antonio patios end up with screen walls on the prevailing breeze side and clear vinyl drop curtains where weather drives in. We can also do hybrid panels — vinyl windows in a screen wall — sewn in our Seguin shop to fit the exact opening.",
          cta: { label: "Drop curtains & wind walls", href: "/curtains" },
        },
        {
          heading: "Motorized Roll-Downs for Large Openings",
          text: "Big covered patios (think 30-foot openings on Stone Oak or Cibolo Canyons builds) need motorized roll-downs — manual crank doesn't scale. We install Somfy-powered roll-downs with wall switches, remotes and optional wind sensors that auto-retract before a storm.",
          cta: { label: "Sun & wind curtains", href: "/curtains" },
        },
      ]}
      applications={[
        "Drop Curtain Patio Enclosures",
        "Screened-In Porches",
        "Motorized Roll-Down Walls",
        "Outdoor Kitchen Wind Walls",
        "Pool Cabana Enclosures",
        "Commercial Patio Bars & Restaurants",
      ]}
      faqs={[
        { q: "Vinyl or screen?", a: "Vinyl blocks wind and rain — best for winter. Phifertex screen blocks bugs, sun and 90% UV with breeze — best March through November in San Antonio. Most patios get both." },
        { q: "Can you motorize?", a: "Yes — Somfy-powered roll-downs with switch, remote or app control. Standard on larger covered patios." },
        { q: "Will it work with my current cover?", a: "Almost always. We mount to existing wood, aluminum or stucco fascia after confirming structure on the measure visit." },
        { q: "Do you build screened porches?", a: "Yes — full Phifertex screen walls with zipper doors framed in welded aluminum." },
      ]}
      cityCallouts={[
        { to: "/san-antonio", city: "San Antonio", anchorText: "All Texas Made Shade services in San Antonio", blurb: "Every awning, cover, sail-shade and enclosure we install in San Antonio." },
        { to: "/curtains", city: "Statewide", anchorText: "Drop curtains & wind walls (statewide)", blurb: "See our full line of clear vinyl and screen drop curtains." },
      ]}
      postCta={<SaSiblingCallouts current={PATH} />}
    />
  );
}
